import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../service/auth.service';
import { NotificationHandler } from '../../helper/notification.handler';
import { UrlHelper } from '../../helper/url.helper';
import { CustomValidators, MismatchErrorStateMatcher } from '../../helper/custom.validators';
import { UserService } from '../../service/user.service';

@Component({
  selector: 'pass-reset-confirm',
  templateUrl: './pass-reset-confirm.component.html',
  styleUrls: ['./pass-reset-confirm.component.scss']
})
export class PassResetConfirmComponent implements OnInit {

  inProgress = false;
  email!: string | null;
  username!: string | null;
  forcedCode: boolean = false;

  emailField = new FormControl({value: '', disabled: true}, [
    Validators.email, Validators.required
  ]);
  codeField!: FormControl;

  passwordField = new FormControl('', [CustomValidators.password]);
  confirmPassField = new FormControl('', [Validators.required]);

  resetConfirmForm!: FormGroup;
  matcher = new MismatchErrorStateMatcher();

  constructor(private route: ActivatedRoute,
              private router: Router,
              private authService: AuthService,
              private userService: UserService,
              private formBuilder: FormBuilder,) {
  }

  async ngOnInit() {
    this.email = decodeURIComponent(this.route.snapshot.paramMap.get('email') || '');
    this.username = this.route.snapshot.paramMap.get('username');
    this.emailField.setValue(this.email);
    this.forcedCode = this.route.snapshot.queryParamMap.get('forced_code') === 'true';

    this.codeField = this.forcedCode
      ? new FormControl('', [
        Validators.required, Validators.pattern('[A-Za-z0-9]*'), Validators.minLength(6), Validators.maxLength(10)
      ])
      :
      new FormControl('', [
        Validators.required, Validators.pattern('[0-9]*'), Validators.minLength(6), Validators.maxLength(6)
      ]);

    this.resetConfirmForm = this.formBuilder.group({
      email: this.emailField,
      code: this.codeField,
      password: this.passwordField,
      confirmPass: this.confirmPassField
    }, {validators: CustomValidators.passwordsMatch});

    const session = await this.authService.getSession();
    if (session && session.isValid()) {
      NotificationHandler.handleWarning($localize`:General|This page is not available for authorized users@@general-this-page-is-not-available-for-authorized-users-message:This page is not available for authorized users message`);
      await this.router.navigate([UrlHelper.DEFAULT_AUTH_PATH], {replaceUrl: true});
    }
  }

  shouldDisableSubmit() {
    return !this.resetConfirmForm.valid;
  }

  get signInUrl() {
    return UrlHelper.SIGN_IN_PATH;
  }

  resetConfirm() {
    this.inProgress = true;

    if (this.forcedCode) {
      this.authService.signIn(this.username || '', this.codeField.value as string)
        .then(async user => {
          const changed = await this.authService.completeNewPassword(user, this.passwordField.value as string);
          if (changed) {
            NotificationHandler.notification($localize`:Password Reset Confirm|Password Reset successful message@@password-reset-successful-message:Your password has changed successfully`);

            // const u = await this.authService.getUser();
            this.userService.reportUpdateUser(user.getUsername(), true).subscribe({
              next: () => {
              }, error: error => {
                NotificationHandler.handleError($localize`:User Profile|User profile update report failed notification@@user-profile-report-failed-notification:Profile update report failed`, error);
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
              }, complete: async () => {
                await this.router.navigate([UrlHelper.DEFAULT_AUTH_PATH], {replaceUrl: true});
              }
            });
          } else {
            NotificationHandler.handleWarning($localize`:Password Reset Confirm|Password Reset failed error message@@password-reset-reset-error:Password reset failed and was not successful`);
            this.inProgress = false;
          }

        })
        .catch(error => {
          NotificationHandler.handleWarning($localize`:Password Reset Confirm|Password Reset failed error message@@password-reset-reset-error:Password reset failed and was not successful`, error);
          this.inProgress = false;
        });
    } else {
      this.authService.resetPasswordConfirm(this.username || '', this.passwordField.value as string, this.codeField.value as string)
        .then(async () => {
          NotificationHandler.notification($localize`:Password Reset Confirm|Password Reset successful message@@password-reset-successful-message:Your password has changed successfully`);

          await this.router.navigate([UrlHelper.SIGN_IN_PATH], {replaceUrl: true});
        })
        .catch((err) => {
          NotificationHandler.handleWarning($localize`:Password Reset Confirm|Password Reset failed error message@@password-reset-reset-error:Password reset failed and was not successful`, err);
          this.inProgress = false;
        });
    }
  }

}
